import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo, useCallback } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import {
  fetchPermissions,
  updatePermission,
  getPermissionById,
  deletePermission,
} from '@/api/permissions.api'
import { buildColumns } from './columns'
import type { PermissionGroupItem } from '@/types/api'
import { PermissionFilterPanel } from './PermissionFilterPanel'
import type { PermissionFilterValues } from './PermissionFilterPanel'
import { PermissionFormDrawer } from './PermissionFormDrawer'
import { PermissionDetailDrawer } from './PermissionDrawer'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { usePermission } from '@/hooks/use-permission'
import { formatStartDate, formatEndDate } from '@/utils/format'
import { exportPermission, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: PermissionFilterValues = {
  keySearch: '',
  time: undefined,
  status: undefined,
}

export function PermissionsPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const canCreate = usePermission('permission-create')
  const canView = usePermission('permission-view')
  const canUpdate = usePermission('permission-update')
  const canDelete = usePermission('permission-delete')
  const canExport = usePermission('permission-export')

  const [showFilter, setShowFilter] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [filter, setFilter] = useState<PermissionFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<PermissionFilterValues>(DEFAULT_FILTER)

  // Modal & Form state
  const [viewingItem, setViewingItem] = useState<PermissionGroupItem | null>(
    null,
  )
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingItem, setDeletingItem] = useState<PermissionGroupItem | null>(
    null,
  )
  const [togglingItem, setTogglingItem] = useState<PermissionGroupItem | null>(
    null,
  )

  const queryClient = useQueryClient()

  const { data, refetch } = useQuery({
    queryKey: ['permissions', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) => {
      return fetchPermissions(
        pageIndex,
        pageSize,
        {
          keySearch: activeFilters.keySearch || undefined,
          status: activeFilters.status,
          fromDate: formatStartDate(activeFilters.time?.from) || undefined,
          toDate:
            formatEndDate(activeFilters.time?.to || activeFilters.time?.from) ||
            undefined,
        },
        signal,
      )
    },
    refetchOnMount: 'always',
  })

  const hasData = Boolean(data?.items && data.items.length > 0)

  const { mutate: toggleStatus, isPending: isToggling } = useMutation({
    mutationFn: async (item: PermissionGroupItem) => {
      const detail = await getPermissionById(item.id)

      const unitIds = detail.unitList.map((u) => u.id)
      const permIds = detail.categoryPermissionList
        .flatMap((c) => c.permissionList)
        .filter((p) => p.isSelected)
        .map((p) => p.id)

      return updatePermission(item.id, {
        name: detail.name,
        description: detail.description || '',
        newUnitIds: unitIds,
        oldUnitIds: unitIds,
        newPermissionIds: permIds,
        oldPermissionIds: permIds,
        status: item.status === 1 ? 0 : 1,
      })
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật trạng thái thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
    },
    onSettled: () => {
      setTogglingItem(null)
    },
  })

  const { mutate: removeItem, isPending: isRemoving } = useMutation({
    mutationFn: (item: PermissionGroupItem) => deletePermission(item.id),
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Xóa nhóm quyền thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
    },
    onSettled: () => {
      setDeletingItem(null)
    },
  })

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        showUnitColumn,
        canView,
        canUpdate,
        canDelete,
        onViewDetail: (item) => setViewingItem(item),
        onUpdate: (item) => {
          setFormMode('update')
          setEditingId(item.id)
          setIsFormDrawerOpen(true)
        },
        onDelete: (item) => setDeletingItem(item),
        onToggleStatus: (item) => setTogglingItem(item),
      }),
    [pageIndex, pageSize, showUnitColumn, canView, canUpdate, canDelete],
  )

  // Gọi API xuất danh sách Nhóm quyền
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      try {
        setIsExporting(true)
        await exportPermission(
          {
            page: pageIndex,
            size: pageSize,
            keySearch: activeFilters.keySearch || undefined,
            status: activeFilters.status,
            fromDate: formatStartDate(activeFilters.time?.from) || undefined,
            toDate:
              formatEndDate(activeFilters.time?.to || activeFilters.time?.from) ||
              undefined,
          },
          payload,
          'Danh_sach_nhom_quyen',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [pageIndex, pageSize, activeFilters],
  )

  const { handleExport } = useTableExport(
    'Danh sách nhóm quyền',
    columns,
    handleExportApi,
  )

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH NHÓM QUYỀN"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
      >
        {canCreate && (
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 h-10"
            onClick={() => {
              setFormMode('create')
              setEditingId(null)
              setIsFormDrawerOpen(true)
            }}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        )}
      </PageHeader>

      {showFilter && (
        <PermissionFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              keySearch: filter.keySearch.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          tableOptions={{
            state: { pagination: { pageIndex, pageSize } },
            onPaginationChange: setPagination,
            getPaginationRowModel: getPaginationRowModel(),
            getCoreRowModel: getCoreRowModel(),
            manualPagination: true,
            pageCount: data?.totalPages,
          }}
          footer={
            <Pagination
              page={pageIndex + 1}
              pageCount={data?.totalPages ?? 1}
              pageSize={pageSize}
              totalCount={data?.total ?? 0}
              onPageChange={(p) =>
                setPagination((s) => ({ ...s, pageIndex: p - 1 }))
              }
              onPageSizeChange={(size) =>
                setPagination({ pageIndex: 0, pageSize: size })
              }
            />
          }
        />
      </div>

      <PermissionDetailDrawer
        permissionId={viewingItem?.id || null}
        onClose={() => setViewingItem(null)}
        showUnitColumn={showUnitColumn}
        onUpdate={(item) => {
          setViewingItem(null)
          setFormMode('update')
          setEditingId(item.id)
          setIsFormDrawerOpen(true)
        }}
      />

      {isFormDrawerOpen && (
        <PermissionFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          permissionId={editingId}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingId(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!togglingItem}
        title="Xác nhận"
        description={
          <>
            Bạn chắc chắn muốn{' '}
            {togglingItem?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} nhóm
            quyền <strong>{togglingItem?.name}</strong> này?
          </>
        }
        variant="info"
        isLoading={isToggling}
        onClose={() => setTogglingItem(null)}
        onConfirm={() => togglingItem && toggleStatus(togglingItem)}
      />

      <ConfirmModal
        isOpen={!!deletingItem}
        title="Xóa nhóm quyền"
        description={
          <>
            Bạn chắc chắn muốn xóa nhóm quyền{' '}
            <strong>{deletingItem?.name}</strong> này?
          </>
        }
        variant="danger"
        isLoading={isRemoving}
        onClose={() => setDeletingItem(null)}
        onConfirm={() => deletingItem && removeItem(deletingItem)}
      />
    </section>
  )
}
